import { ApiError, apiRequest } from './client.js';

/**
 * Branding work orders (M17). Approval is not done here: submitting hands the
 * order to whichever workflow the tenant bound to
 * BRANDING_WORK_ORDER_APPROVAL, and the Branding Head acts from the inbox.
 */

export interface BrandingAttributeOption {
  readonly optionId: string;
  readonly optionCode: string;
  readonly optionName: string;
  readonly displayOrder: number;
}

/**
 * An optional detail a line may carry, e.g. Size or Illumination.
 *
 * A null brandingTypeId and brandingItemId means the attribute is offered on
 * every line; either one set narrows it to that branding work.
 */
export interface BrandingAttribute {
  readonly attributeId: string;
  readonly attributeCode: string;
  readonly attributeName: string;
  readonly dataType: 'TEXT' | 'NUMBER' | 'DATE' | 'LIST' | string;
  readonly displayOrder: number;
  readonly brandingTypeId: string | null;
  readonly brandingItemId: string | null;
  readonly options: readonly BrandingAttributeOption[];
}

/** One answer against one attribute on one line. */
export interface WorkOrderLineAttributeValue {
  readonly attributeId: string;
  readonly attributeCode?: string;
  readonly attributeName?: string;
  readonly dataType?: string;
  readonly optionId?: string | null;
  readonly optionCode?: string | null;
  readonly optionName?: string | null;
  readonly valueText?: string | null;
  readonly valueNumber?: string | number | null;
  readonly valueDate?: string | null;
}

export interface WorkOrderLine {
  readonly workOrderLineId?: string;
  readonly lineNumber?: number;
  readonly brandingTypeId: string;
  readonly typeName?: string;
  readonly brandingItemId?: string | null;
  readonly itemName?: string | null;
  readonly otherDetail?: string | null;
  readonly geoLevelCode?: string | null;
  readonly geoNodeId?: string | null;
  readonly geoNodeCode?: string | null;
  readonly geoNodeName?: string | null;
  readonly quantity: number | string;
  /** Height. Sent only for work measured by area; 1 otherwise. */
  readonly dim1?: number | string | null;
  /** Width. Sent only for work measured by area; 1 otherwise. */
  readonly dim2?: number | string | null;
  /** Slot length. Sent only for broadcast work; never priced. */
  readonly dim3?: number | string | null;
  readonly dim3UomCode?: string | null;
  readonly variantOptionId?: string | null;
  readonly variantName?: string | null;
  /** The tax code the line is billed under; defaults from the sub-type. */
  readonly hsnSacCode?: string | null;
  readonly unitOfMeasure: string;
  /** The tenant's name for unitOfMeasure (t602); read-only, from the detail. */
  readonly uomName?: string | null;
  /** The tenant's name for dim3UomCode (t602); read-only, from the detail. */
  readonly dim3UomName?: string | null;
  readonly rateId?: string | null;
  readonly rateValue: number | string;
  readonly additionalAmount?: number | string | null;
  readonly additionalRemark?: string | null;
  readonly lineAmount?: string;
  /** Built by the shared rules and stored, so the bill reads what was agreed. */
  readonly lineDescription?: string | null;
  /**
   * Which budget pays for this line, judged at the depth that budget was drawn.
   * The vocabulary is the database's own (t620_budget_match_chk): UNASSESSED
   * until the order is submitted, then MATCHED / UNFUNDED / AMBIGUOUS, or
   * TRANSFER where a person has moved it to another budget.
   */
  readonly budgetMatchCode?: string | null;
  readonly fundingPlanId?: string | null;
  readonly fundingPlanName?: string | null;
  /** The budget bucket's members, e.g. "BTL · Rajasthan" — the depth it is funded at. */
  readonly fundingBucketLabel?: string | null;
  readonly fundingDimensionSignature?: string | null;
  readonly budgetExceptionReason?: string | null;
  readonly remarks?: string | null;
  readonly attributes?: readonly WorkOrderLineAttributeValue[];
}

export interface WorkOrderSummary {
  readonly workOrderId: string;
  readonly woNumber: string;
  readonly subject: string;
  readonly woStatusCode: string;
  readonly validFromDate: string;
  readonly validToDate: string;
  readonly totalAmount: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly cycleCode: string;
  /** Where the order has reached, when it is still waiting on somebody. */
  readonly workflowInstanceId: string | null;
  readonly pendingLevel: number | null;
  readonly totalLevels: number | null;
  readonly pendingRoleCode: string | null;
  readonly pendingWith: string | null;
}

export interface WorkOrderDetail extends WorkOrderSummary {
  readonly vendorId: string;
  readonly planningCycleId: string;
  readonly remarks: string | null;
  readonly otherExpensesAllowed?: boolean;
  readonly workflowInstanceId: string | null;
  readonly lines: readonly WorkOrderLine[];
}

/** Budget position; submission additionally carries the configured decision. */
export interface WorkOrderBudgetStatus {
  readonly outcome:
    | 'WITHIN_BUDGET'
    | 'BUDGET_EXCEEDED'
    | 'BUDGET_NOT_CONFIGURED'
    | 'BUDGET_NOT_LINKED'
    | 'CHECK_UNAVAILABLE';
  readonly warning: string | null;
  readonly budgetAmount: number;
  readonly committedAmount: number;
  readonly thisOrderAmount: number;
  readonly projectedAmount?: number;
  readonly decision?: 'ALLOW' | 'WARN' | 'BLOCK';
  readonly decisionProjectedAmount?: number;
  readonly tolerancePct?: number;
  readonly policy?: 'PREVENT' | 'WARN' | 'ALLOW_WITHIN_TOLERANCE' | 'ALLOW';
}

/** Leave unrelated or malformed errors to the normal error presentation. */
export function budgetControlStatus(error: unknown): WorkOrderBudgetStatus | null {
  if (!(error instanceof ApiError) || !error.body || typeof error.body !== 'object' ||
      !('code' in error.body) || error.body.code !== 'EX-WO-16' ||
      !('budgetStatus' in error.body)) return null;
  const status = error.body.budgetStatus;
  if (!status || typeof status !== 'object' || !('outcome' in status) ||
      !('decision' in status) || status.decision !== 'BLOCK' ||
      !('warning' in status) || (status.warning !== null && typeof status.warning !== 'string') ||
      !('budgetAmount' in status) || typeof status.budgetAmount !== 'number' || !Number.isFinite(status.budgetAmount) ||
      !('committedAmount' in status) || typeof status.committedAmount !== 'number' || !Number.isFinite(status.committedAmount) ||
      !('thisOrderAmount' in status) || typeof status.thisOrderAmount !== 'number' || !Number.isFinite(status.thisOrderAmount) ||
      !('decisionProjectedAmount' in status) || typeof status.decisionProjectedAmount !== 'number' || !Number.isFinite(status.decisionProjectedAmount) ||
      !('tolerancePct' in status) || typeof status.tolerancePct !== 'number' || !Number.isFinite(status.tolerancePct)) return null;
  const outcome = status.outcome;
  if (outcome !== 'WITHIN_BUDGET' && outcome !== 'BUDGET_EXCEEDED' &&
      outcome !== 'BUDGET_NOT_CONFIGURED' && outcome !== 'BUDGET_NOT_LINKED' &&
      outcome !== 'CHECK_UNAVAILABLE') return null;
  if (!('policy' in status) ||
      (status.policy !== 'PREVENT' && status.policy !== 'WARN' &&
       status.policy !== 'ALLOW_WITHIN_TOLERANCE' && status.policy !== 'ALLOW')) return null;
  return {
    outcome,
    warning: status.warning,
    budgetAmount: status.budgetAmount,
    committedAmount: status.committedAmount,
    thisOrderAmount: status.thisOrderAmount,
    decision: status.decision,
    decisionProjectedAmount: status.decisionProjectedAmount,
    tolerancePct: status.tolerancePct,
    policy: status.policy,
  };
}

export interface SubmitWorkOrderResult extends WorkOrderDetail {
  readonly budgetStatus?: WorkOrderBudgetStatus;
}

/**
 * Creating carries the same budget position submitting does. A draft is built
 * against a real budget, so the reader is told at once if there is no room —
 * or no budget drawn for what the order asks for.
 */
export type CreateWorkOrderResult = SubmitWorkOrderResult;

export interface AllottedRow {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly cycleCode: string;
  readonly typeCode: string;
  readonly typeName: string;
  readonly itemCode: string | null;
  readonly itemName: string | null;
  readonly geoLevelCode: string | null;
  readonly geoNodeId: string | null;
  readonly unitOfMeasure: string;
  readonly allottedQuantity: string;
  readonly allottedAmount: string;
  readonly workOrderCount: string;
}

export interface CreateWorkOrderInput {
  readonly vendorId: string;
  readonly planningCycleId: string;
  readonly validFromDate: string;
  readonly validToDate: string;
  readonly subject?: string;
  readonly remarks?: string;
  /** Whether the vendor may bill expenses beyond the agreed rate. */
  readonly otherExpensesAllowed?: boolean;
  readonly lines: readonly Omit<WorkOrderLine, 'workOrderLineId' | 'lineNumber' | 'typeName' | 'itemName' | 'lineAmount' | 'uomName' | 'dim3UomName'>[];
}

type Tok = string | null | undefined;

const BASE = '/v1/m17/work-orders';

export function listWorkOrders(
  token?: Tok,
  vendorId?: string,
  planningCycleId?: string,
): Promise<{ data: readonly WorkOrderSummary[]; total: number }> {
  const qs = new URLSearchParams();
  if (vendorId) qs.set('vendorId', vendorId);
  if (planningCycleId) qs.set('planningCycleId', planningCycleId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest<{ data: readonly WorkOrderSummary[]; total: number }>(
    `${BASE}${suffix}`,
    { token },
  );
}

export function getWorkOrder(id: string, token?: Tok): Promise<WorkOrderDetail> {
  return apiRequest<WorkOrderDetail>(`${BASE}/${encodeURIComponent(id)}`, { token });
}

/**
 * The optional attributes a line may carry. Called without a branding type so
 * the whole registry arrives once and the form can narrow it as the user
 * switches type, rather than asking again for every line.
 */
export function listLineAttributes(
  token?: Tok,
  brandingTypeId?: string,
  brandingItemId?: string,
): Promise<readonly BrandingAttribute[]> {
  const qs = new URLSearchParams();
  if (brandingTypeId) qs.set('brandingTypeId', brandingTypeId);
  if (brandingItemId) qs.set('brandingItemId', brandingItemId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest<readonly BrandingAttribute[]>(`${BASE}/attributes${suffix}`, { token });
}

/**
 * The approved work order lines that cover a sub-type today, so an order can
 * be raised against one. An empty list is the answer "issue a work order
 * first", not an error.
 */
export interface WorkOrderCoverLine {
  readonly workOrderId: string;
  readonly workOrderLineId: string;
  readonly woNumber: string;
  readonly validFromDate: string | null;
  readonly validToDate: string | null;
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly lineNumber: number;
  readonly geoNodeName: string | null;
  readonly committedQuantity: string;
  readonly orderedQuantity: string;
  /** Committed less what earlier orders already drew — what is still free. */
  readonly balanceQuantity: string;
  readonly uomCode: string | null;
  readonly rateValue: string | null;
  readonly lineAmount: string | null;
}

export function listWorkOrderCover(
  brandingTypeId: string,
  brandingItemId: string,
  token?: Tok,
  /**
   * The dealer's district. Supplied, the list is narrowed to vendors whose
   * branding profile covers that place — a profile naming a state covers every
   * district in it, one naming a district covers only that district.
   */
  districtCode?: string,
  /**
   * A line of an order already placed. Supplied, only the vendor that order
   * already goes to is offered: one job goes to one vendor.
   */
  requestLineId?: string,
): Promise<{ data: readonly WorkOrderCoverLine[]; total: number }> {
  const qs = new URLSearchParams({ brandingTypeId, brandingItemId });
  if (districtCode) qs.set('districtCode', districtCode);
  if (requestLineId) qs.set('requestLineId', requestLineId);
  return apiRequest<{ data: readonly WorkOrderCoverLine[]; total: number }>(
    `${BASE}/cover?${qs.toString()}`,
    { token },
  );
}

/** The calling vendor's own approved orders; the vendor comes from the session. */
export function listMyWorkOrders(
  token?: Tok,
  planningCycleId?: string,
): Promise<{ data: readonly WorkOrderSummary[]; total: number }> {
  const suffix = planningCycleId
    ? `?planningCycleId=${encodeURIComponent(planningCycleId)}`
    : '';
  return apiRequest<{ data: readonly WorkOrderSummary[]; total: number }>(
    `${BASE}/mine${suffix}`,
    { token },
  );
}

export function createWorkOrder(
  body: CreateWorkOrderInput,
  token?: Tok,
): Promise<CreateWorkOrderResult> {
  return apiRequest<CreateWorkOrderResult>(BASE, { method: 'POST', token, body });
}

export function submitWorkOrder(id: string, token?: Tok): Promise<SubmitWorkOrderResult> {
  return apiRequest<SubmitWorkOrderResult>(`${BASE}/${encodeURIComponent(id)}/submit`, {
    method: 'POST',
    token,
    body: {},
  });
}

export function cancelWorkOrder(
  id: string,
  reason: string,
  token?: Tok,
): Promise<WorkOrderDetail> {
  return apiRequest<WorkOrderDetail>(`${BASE}/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    token,
    body: { reason },
  });
}

export function listAllotted(
  token?: Tok,
  planningCycleId?: string,
  vendorId?: string,
): Promise<readonly AllottedRow[]> {
  const qs = new URLSearchParams();
  if (planningCycleId) qs.set('planningCycleId', planningCycleId);
  if (vendorId) qs.set('vendorId', vendorId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest<readonly AllottedRow[]>(`${BASE}/allotted${suffix}`, { token });
}

export interface GeoNodeOption {
  readonly id: string;
  readonly code: string;
  readonly name: string;
}

/** Nodes at one geography level, narrowed to a parent node for child levels. */
export function listGeographyNodes(
  level: string,
  parentId: string | undefined,
  token?: Tok,
): Promise<readonly GeoNodeOption[]> {
  const qs = new URLSearchParams({ level });
  if (parentId) qs.set('parentId', parentId);
  return apiRequest<readonly GeoNodeOption[]>(`${BASE}/geography?${qs.toString()}`, {
    token,
  });
}
